<template>
  <div>
    <div class="cal-sans-regular black--text font-size-28 pb-2 mt-3" style="border-bottom: 3px solid #4caf50">Editor</div>

    <div class="d-flex flex-row mt-4">
      <!-- Outline / Pages -->
      <v-sheet class="pa-3 mr-4" color="grey lighten-4" rounded style="width: 280px;">
        <div class="subtitle-2 mb-2">Outline</div>
        <v-list dense nav>
          <v-list-item v-for="(page, index) in pages" :key="'page-' + index" @click="selectPage(index)" :class="{ 'grey lighten-2': index === selectedPageIndex }">
            <v-list-item-content>
              <v-list-item-title>{{ page.title || ('Page ' + (index + 1)) }}</v-list-item-title>
              <v-list-item-subtitle class="text-truncate">{{ page.summary || 'No summary yet' }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon small @click.stop="duplicatePage(index)"><v-icon small>mdi-content-copy</v-icon></v-btn>
              <v-btn icon small @click.stop="deletePage(index)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <v-btn small color="green" dark block @click="addPage">
          <v-icon left small>mdi-plus</v-icon>
          Add Page
        </v-btn>
      </v-sheet>

      <!-- Editor -->
      <div class="flex-1 mr-4">
        <v-text-field v-model="currentPage.title" label="Page Title" dense outlined hide-details class="mb-3"></v-text-field>
        <v-textarea v-model="currentPage.content" outlined auto-grow rows="10" label="Write your story..." :disabled="!currentPage"></v-textarea>
        <div class="d-flex mt-2">
          <v-btn small class="mr-2" @click="aiRewrite('expand')"><v-icon left small>mdi-arrow-expand-all</v-icon>Expand</v-btn>
          <v-btn small class="mr-2" @click="aiRewrite('tighten')"><v-icon left small>mdi-arrow-collapse</v-icon>Tighten</v-btn>
          <v-btn small class="mr-2" @click="aiRewrite('tone')"><v-icon left small>mdi-swap-horizontal</v-icon>Change Tone</v-btn>
          <v-spacer></v-spacer>
          <v-btn small color="black" dark @click="$emit('navigateTab', 1)"><v-icon left small>mdi-earth-box</v-icon>World</v-btn>
          <v-btn small color="black" dark class="ml-2" @click="$emit('navigateTab', 2)"><v-icon left small>mdi-wizard-hat</v-icon>Characters</v-btn>
        </div>
      </div>

      <!-- Right Panel -->
      <v-sheet class="pa-3" color="grey lighten-4" rounded style="width: 280px;">
        <div class="subtitle-2 mb-2">Page Options</div>
        <v-text-field v-model="currentPage.imageUrl" label="Image URL" dense outlined hide-details class="mb-3"></v-text-field>
        <v-switch v-model="currentPage.showImage" label="Show image" dense inset></v-switch>
        <v-divider class="my-3"></v-divider>
        <div class="subtitle-2 mb-2">AI Actions</div>
        <v-btn small block class="mb-2" @click="summarizePage"><v-icon left small>mdi-text-short</v-icon>Summarize</v-btn>
        <v-btn small block class="mb-2" @click="extractEntities"><v-icon left small>mdi-lightbulb-outline</v-icon>Extract Entities</v-btn>
      </v-sheet>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StoryEditor',
  props: {
    story: {
      type: Object,
      required: false,
      default: null,
    },
    pages: {
      type: Array,
      required: true,
    }
  },
  data() {
    return {
      selectedPageIndex: 0,
    }
  },
  computed: {
    currentPage: {
      get() {
        return this.pages[this.selectedPageIndex] || { title: '', content: '', imageUrl: '', showImage: false }
      },
      set(val) {
        const next = [...this.pages]
        next[this.selectedPageIndex] = val
        this.$emit('update:pages', next)
      }
    }
  },
  watch: {
    // Ensure v-model bindings propagate to parent
    'currentPage.title'(val) {
      const updated = { ...this.currentPage, title: val }
      this.currentPage = updated
    },
    'currentPage.content'(val) {
      const updated = { ...this.currentPage, content: val }
      this.currentPage = updated
    },
    'currentPage.imageUrl'(val) {
      const updated = { ...this.currentPage, imageUrl: val }
      this.currentPage = updated
    },
    'currentPage.showImage'(val) {
      const updated = { ...this.currentPage, showImage: val }
      this.currentPage = updated
    },
  },
  methods: {
    selectPage(index) {
      this.selectedPageIndex = index
    },
    addPage() {
      const next = [...this.pages, { title: '', summary: '', content: '', imageUrl: '', showImage: false }]
      this.$emit('update:pages', next)
      this.selectedPageIndex = next.length - 1
    },
    duplicatePage(index) {
      const copy = JSON.parse(JSON.stringify(this.pages[index]))
      const next = [...this.pages]
      next.splice(index + 1, 0, copy)
      this.$emit('update:pages', next)
      this.selectedPageIndex = index + 1
    },
    deletePage(index) {
      if (this.pages.length === 1) return
      const next = [...this.pages]
      next.splice(index, 1)
      this.$emit('update:pages', next)
      if (this.selectedPageIndex >= next.length) this.selectedPageIndex = next.length - 1
    },
    summarizePage() {
      if (!this.currentPage.content) return
      this.$emit('summarizePage', { index: this.selectedPageIndex, content: this.currentPage.content })
    },
    extractEntities() {
      if (!this.currentPage.content) return
      this.$emit('extractEntities', { index: this.selectedPageIndex, content: this.currentPage.content })
    },
    aiRewrite(mode) {
      if (!this.currentPage.content) return
      this.$emit('aiRewrite', { mode, index: this.selectedPageIndex, content: this.currentPage.content })
    }
  }
}
</script>